import React, { useState, useEffect, createContext, useRef, useContext, useCallback, useMemo } from "react";
import CarsNav from "./CarsNav";
import CarsHead from "./CarsHead"
import MessageBox from "./MessageBox";
import TextBox from "./TextBox";
import { emitter } from './UtilityFunctions';
import InviteRequestForm from "./InviteRequestForm";
import RegistrationForm from "./RegistrationForm";
import LogIn from "./LogIn";
import { Navigate, BrowserRouter, Routes, Route } from "react-router-dom";
import ForgotPassword from "./ForgotPassword";
import { Spinner } from "@radix-ui/themes";
import socket from './socket';
import { apiFetch } from './api.js';
import { fetchAiStatus } from './aiApi.js';
import ResetPassword from "./ResetPassword";
import JoyridePortal from "./JoyridePortal";
import CustomTooltip from "./JoyrideCustomTooltip";
import { NotificationProvider } from './NotificationContext';
import { ToastProvider } from './ToastContext';
import PwaUpdater from './PwaUpdater';
import InstallPrompt from './InstallPrompt';
import ConnectionError from './ConnectionError';
import { GlobalContext } from "./GlobalContext";
import { userCan } from "./permissions";
import { usePermissionsRefresh } from "./usePermissionsRefresh";
import { applyTheme, presetFromThemeSettings, themeAccent } from "./theme";

//Stable empty Set so users who can see costs don't churn hiddenCostFields.
const EMPTY_SET = new Set();
import { AuthProvider, useAuth } from "./AuthContext";
import ChangePasswordModal from "./ChangePassword";
import PasskeyPrompt from "./PasskeyPrompt";
import WelcomeTourPrompt from "./WelcomeTourPrompt";
import SetupPrompt from "./SetupPrompt";
import useGlobalTour from "./useGlobalTour";
import useTourRegistration from "./hooks/useTourRegistration";
import { setToken } from './csrf.js';
import ErrorBoundary from "./ErrorBoundary";
import AccountSuspended from "./AccountSuspended";

function Cars(){
    return (
        <AuthProvider>
            <CarsApp />
        </AuthProvider>
    );
}

function CarsApp(){
    //DealershipID, UserID, FirstName, LastName, Phone1, Phone2, EmailAddress, Preferences
    const [ logInStatus, setLogInStatus ] = useState("wait"); //true | false | wait
    //Set by the 402 handler in api.js. Holds the SERVER's wording, so the
    //explanation a tenant reads is the one billing actually enforces.
    const [ suspended, setSuspended ] = useState(null);
    const [ isLoading, setIsLoading ] = useState(true);
    //The startup /getuserdata call could not be answered — no network, or the
    //API is unreachable. Distinct from `suspended` (a deliberate 402) and from
    //being logged out (a 401): neither of those means "try again later".
    const [ connError, setConnError ] = useState(false);
    //Mirrors connError for the 'online' listener, which is registered once on
    //mount and would otherwise close over the initial `false` forever.
    const connErrorRef = useRef(false);
    const markConnError = (failed) => {
        connErrorRef.current = failed;
        setConnError(failed);
    };
    const { session, setSessionData, updateCompany, updateUser, updateFieldOrder, setAiEnabled } = useAuth();

    const serverDisconnectHandler = ()=>{
        //Phones routinely kill the socket when the app is backgrounded — stay
        //quiet then; the visibility-resume handler below reconnects silently
        //and only a failure while the user is actually looking should alert.
        if(document.visibilityState !== "visible") return;
        //Says "was not saved", not just "no connection". sendOrFail has already
        //reverted the field on screen, and the user needs to know the edit is
        //gone rather than pending — otherwise they walk away believing it landed,
        //which is the failure this whole change exists to remove.
        toggleMessageBox({
            on: true,
            title: "Not saved — no connection",
            message: "Your change could not be sent to the server, so it has been undone. "
                + "Check your connection and try again.",
            buttons: ["OK"],
            clicked: ""
        });
    }
    useEffect(()=>{
        //If login status changes to true, update socket
        //This time include auth in the options object
        if(logInStatus!==true) return;
        socket.auth = {
            DealershipID: session.DealershipID,
            UserID: session.UserID
        };
        socket.disconnect();
        socket.connect();

        return ()=>{
            socket.disconnect();
        }
    },[logInStatus, session.DealershipID, session.UserID])

    //Mobile lifecycle (plan 5.3): when the tab returns from a background
    //suspend (visibilitychange) or a bfcache restore (pageshow), bring the
    //socket back up if the OS killed it. Sheet data itself refreshes via
    //react-query's default refetchOnWindowFocus.
    useEffect(()=>{
        if(logInStatus!==true) return;
        const resumeSocket = ()=>{
            if(document.visibilityState === "visible" && !socket.connected){
                socket.connect();
            }
        };
        document.addEventListener("visibilitychange", resumeSocket);
        window.addEventListener("pageshow", resumeSocket);
        return ()=>{
            document.removeEventListener("visibilitychange", resumeSocket);
            window.removeEventListener("pageshow", resumeSocket);
        }
    },[logInStatus])

    // Probe AI availability once per login — non-blocking.
    // Covers both the silent /getuserdata path and the explicit /login path.
    // On any failure (network, 401, 503) defaults to false so AiButton stays hidden.
    useEffect(()=>{
        if(logInStatus !== true) return;
        fetchAiStatus()
            .then(({ enabled }) => setAiEnabled(enabled))
            .catch(() => { /* default false already set in AuthContext */ });
    },[logInStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    //Tenant theme: re-applied whenever the dealership's setting changes
    //(login populates it; the Appearance settings save updates it).
    useEffect(()=>{
        applyTheme(presetFromThemeSettings(session.companyData?.ThemeSettings));
    },[session.companyData?.ThemeSettings]);

    useEffect(()=>{ //Get the userdata
        //Listen for disconnection events and show a message
        emitter.on("server-disconnected",serverDisconnectHandler);
        getUserData();

        const handleSessionExpired = () => {
            setToken(null);
            setLogInStatus(false);
        };
        window.addEventListener('session-expired', handleSessionExpired);

        //Recover by itself when the connection comes back. Without this an
        //installed app opened on a dead network stayed on the error screen
        //even once the signal returned, because nothing ever retried.
        const handleBackOnline = () => {
            if (connErrorRef.current) getUserData();
        };
        window.addEventListener('online', handleBackOnline);

        const handleSuspended = (e) => setSuspended(e.detail?.message || true);
        window.addEventListener('account-suspended', handleSuspended);

        const handleRenderError = (e) => {
            // Only show a dialog if no dialog is already open (avoids double-dialog
            // when the component's own fetch handler already showed one).
            toggleMessageBox(prev => {
                if (prev.on) return prev;
                return {
                    on: true,
                    title: "Unexpected Error",
                    message: e.detail?.message || "An unexpected error occurred.",
                    buttons: ["OK"],
                    clicked: ""
                };
            });
        };
        window.addEventListener('app-render-error', handleRenderError);

        return ()=>{
            emitter.removeListener("server-disconnected",serverDisconnectHandler);
            window.removeEventListener('session-expired', handleSessionExpired);
            window.removeEventListener('online', handleBackOnline);
            window.removeEventListener('account-suspended', handleSuspended);
            window.removeEventListener('app-render-error', handleRenderError);
        }
    },[]);

//Message Box
    const [ msgBoxStatus, toggleMessageBox ] = useState({
        on: false,
        title: "",
        message: "",
        buttons: ""
    });
    const [ textBoxStatus, toggleTextBox ] = useState(
        {
            on: false,
            title: ""
        }
    )
    //The pending dialog's resolve() lives in a ref, not state: nothing renders
    //it, so storing it in state only bought an extra whole-tree render each
    //time a dialog opened and closed. A ref also keeps the click handlers
    //stable, which matters because getMsgBoxResult/asyncOpenTextBox go into the
    //context value below.
    const resolverRef = useRef(null);

    //Clearing the ref before resolving means a dialog can only ever settle
    //once, which the old state-based version did not guarantee.
    const settleDialog = useCallback((value)=>{
        const resolve = resolverRef.current;
        resolverRef.current = null;
        if (resolve) resolve(value);
    },[]);

    const handleMsgBoxClick = useCallback((value)=>{
        toggleMessageBox(prev => ({...prev, on: false}));
        settleDialog(value);
    },[settleDialog]);
    const getMsgBoxResult = useCallback((msgBoxState)=>{
        return new Promise((resolve)=>{
            resolverRef.current = resolve;
            toggleMessageBox(msgBoxState);
        })
    },[]);

    const handleTextBoxClick = useCallback((value)=>{
        toggleTextBox(prev => ({...prev, on: false}));
        settleDialog(value);
    },[]);
    const asyncOpenTextBox = useCallback((textBoxState)=>{
        return new Promise((resolve)=>{
            resolverRef.current = resolve;
            toggleTextBox(textBoxState);
        });
    },[]);
    const getUserData = ()=>{
        markConnError(false);
        apiFetch('/getuserdata', { body: {} }).then(async (res)=>{
            // console.log(res);
            switch(res.status){
                case 200: //OK
                    const resObj = await res.json();
                    setToken(resObj.csrfToken);
                    setLogInStatus(true);
                    //User preferences come as a string. Parse it into an object
                    let prefs = resObj.userData.Preferences;
                    if(!prefs){
                        prefs = {};
                    } else {
                        Object.keys(prefs)
                        for(let sheetname of Object.keys(prefs)){
                            prefs[sheetname] = JSON.parse(prefs[sheetname]);
                        }     
                    }
                    resObj.userData.Preferences = prefs;

                    setSessionData(resObj.userData);
                    //Now turn off the spinner
                    setIsLoading(false);

                    //Now we have the DealershipID, set the socket
                    break;
                case 500: //Server error
                    console.error(`Server Error: ${await res.text()}`);
                    markConnError(true);
                    setIsLoading(false);
                    break;
                case 401: //Unauthorised. Not logged in
                    setLogInStatus(false);
                    setIsLoading(false)
                    break;
                default:
                    console.error(`Unexpected response status: ${res.status}`);
                    markConnError(true);
                    setIsLoading(false);
            }
        }).catch((err)=>{
            //No network, DNS failure, or the API is down. Every branch here
            //MUST clear isLoading: this used to alert() and return, leaving
            //isLoading true forever, so an installed PWA launched with no
            //signal sat on the spinner below with no address bar to reload
            //from and no way out but force-quitting. The offline screen and
            //the "online" listener in the mount effect are what replaced it.
            console.error("getuserdata failed:", err);
            markConnError(true);
            setIsLoading(false);
        });
    }

    //PERMISSIONS CHANGED UNDER US. See usePermissionsRefresh for the whole
    //story: both PermList and CostGatedFields used to be loaded once at sign-in
    //and never refreshed, so an admin's change reached this tab only on reload.
    //getUserData re-reads both from the one payload they share, and re-reads the
    //CSRF token without minting a new one (the server only generates when the
    //session has none), so nothing already in flight is invalidated.
    usePermissionsRefresh(socket, logInStatus === true, getUserData);

    //Global unsaved-changes registry: a view registers a predicate reporting
    //whether it has pending edits, so navigation can warn before leaving it.
    const navGuardsRef = useRef(new Set());
    const registerNavGuard = useCallback((fn) => {
        navGuardsRef.current.add(fn);
        return () => navGuardsRef.current.delete(fn);
    }, []);
    const hasUnsavedChanges = useCallback(() => {
        for (const fn of navGuardsRef.current) {
            try { if (fn()) return true; } catch { /* ignore a bad guard */ }
        }
        return false;
    }, []);

    const logOut = useCallback(() => setLogInStatus(false), []);

    //Every one of the 57 files that reads GlobalContext re-renders whenever this
    //object changes identity, and memoized consumers cannot bail out of it. It
    //used to be an inline literal holding freshly-allocated closures and a
    //`new Set(...)`, so it changed on every render of this component — including
    //the two renders a message box costs just by opening and closing.
    //
    //`msgBoxStatus` deliberately is NOT in here any more. It was the single
    //most frequently changing member, and no consumer ever read it: the box is
    //rendered from local state below, and the only two files that named it
    //(AddEditCommitted, CustomCombobox) destructured it and never used it.
    const globalValue = useMemo(() => ({
        ...session,
        socket,
        toggleMessageBox, getMsgBoxResult, asyncOpenTextBox,
        logOut,
        updateCompany, updateUser, updateFieldOrder,
        registerNavGuard, hasUnsavedChanges,
        can: (...perms) => userCan(session.userData, ...perms),
        hiddenCostFields: userCan(session.userData, 'inventory.costs')
            ? EMPTY_SET
            : new Set(session.userData?.CostGatedFields || []),
    }), [session, getMsgBoxResult, asyncOpenTextBox, logOut,
         updateCompany, updateUser, updateFieldOrder,
         registerNavGuard, hasUnsavedChanges]);

    //Checked BEFORE the spinner: a failed startup call leaves logInStatus at
    //"wait", so the spinner branch would otherwise swallow this and we would
    //be back to the endless spinner it replaced.
    if(connError && logInStatus !== true){
        return <ConnectionError onRetry={getUserData} />;
    }

    if(logInStatus==="wait" || isLoading){ //Or user data is not yet fetched
        console.log("Login status: ", logInStatus);
        return(
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <Spinner size="3" />
            </div>
        )
    }

    //Suspension replaces the app rather than decorating it: every other screen
    //is 402ing anyway, so leaving them mounted only shows a tenant a wall of
    //failures next to the explanation for them.
    if (suspended && logInStatus === true) {
        return <AccountSuspended
            message={typeof suspended === 'string' ? suspended : undefined}
            onSignOut={() => { setSuspended(null); setLogInStatus(false); }} />;
    }

    return (
    <>
        <GlobalContext.Provider value={globalValue} >
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={logInStatus===true ?
                        <ErrorBoundary label="home">
                            <Home setLogInStatus={setLogInStatus} logInStatus={logInStatus}/>
                        </ErrorBoundary> :
                        <LogIn setLogInStatus={setLogInStatus} setUserData={setSessionData}/>
                    } />
                    <Route 
                        path="/login" 
                        element={ logInStatus===true ? 
                            <Navigate to="/" replace /> :
                            <LogIn setLogInStatus={setLogInStatus} setUserData={setSessionData}/>
                    } />
                    <Route
                        path="/reset-password"
                        element={<ResetPassword />}
                    />
                    <Route path="/signup" element={
                        <RegistrationForm setLogInStatus={setLogInStatus} />
                    } />
                    <Route path="/forgotpassword" element={
                        <ForgotPassword />
                    } />
                    <Route path="/request-invite" element={
                        <InviteRequestForm />
                    } />
                </Routes>
            </BrowserRouter>
        </GlobalContext.Provider>

        {msgBoxStatus.on && <MessageBox
            title={msgBoxStatus.title}
            message={msgBoxStatus.message}
            buttons={msgBoxStatus.buttons}
            onClick={handleMsgBoxClick}

        />}
        {textBoxStatus.on && <TextBox
            title={textBoxStatus.title}
            onClick={handleTextBoxClick}  
        />}
    </>
    );
}

export default Cars;

function Home({ setLogInStatus, logInStatus }) {
    const globals = useContext(GlobalContext);
    const [showChangePassword, setShowChangePassword] = useState(false);

    // The app overview. Lives in a hook rather than inline here so it has the
    // same shape as every other tour, and so the header's help menu can start
    // it through the registry instead of the company logo carrying a hidden
    // onClick.
    const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } = useGlobalTour();
    useTourRegistration('overview', handleStartTutorial);


      // Check for first login on mount
    useEffect(() => {
        if (globals.userData?.FirstLogin) {
        setShowChangePassword(true);
        }
    }, [globals.userData?.FirstLogin]);

    const handlePasswordChangeClose = () => {
        // Don't allow closing if it's first login
        if (globals.userData?.FirstLogin) {
        return;
        }
        setShowChangePassword(false);
    };
    
    return(
        <ToastProvider>
        <>
            {/* Watches for new deploys and applies them; renders nothing.
                Inside ToastProvider because it prompts through the toast. */}
            <PwaUpdater />
            {/* Offers to install the app to the device. Here rather than
                alongside <LogIn> deliberately: installability depends on the
                service worker, which PwaUpdater only registers once someone is
                signed in, so there is nothing to offer a signed-out visitor. */}
            <InstallPrompt />
            <JoyridePortal
                steps={steps}
                stepIndex={stepIndex}
                run={runTour}
                continuous
                showProgress
                showSkipButton
                styles={{
                options: {
                    primaryColor: themeAccent(),
                    zIndex: 10000,
                },
                }}
                callback={handleJoyrideCallback}
                disableOverlayClose={true}
                spotlightClicks={true}
                locale={{ last: "End Tour", skip: "Skip Tour" }}
                tooltipComponent={CustomTooltip}
                spotlightPadding={3}
            />            
        
            <div className="cars">
                <NotificationProvider>
                    <CarsHead />
                    {/* tourRunning: while the global tour runs, the tab strip renders
                        all tabs (no More overflow) so the #tab-* step targets exist. */}
                    <CarsNav setLogInStatus={setLogInStatus} logInStatus={logInStatus} tourRunning={runTour} />
                </NotificationProvider>
            </div>

            {/* Add ChangePassword modal */}
            <ChangePasswordModal
                isOpen={showChangePassword}
                onClose={handlePasswordChangeClose}
                isFirstLogin={globals.userData?.FirstLogin}
            />

            {/* Offers biometric sign-in once per user per device. Decides for
                itself whether to appear at all — including standing down while
                the undismissable first-login password change is up. */}
            <PasskeyPrompt />

            {/* Offers the app overview once per user per device. Like the
                passkey prompt, it decides for itself whether to appear —
                including waiting its turn behind that one. */}
            <WelcomeTourPrompt />

            {/* What this dealership still has not configured, once a session
                for whoever can fix it. Last in the queue on purpose: a chore
                list shown before someone has seen the app gets dismissed
                unread. Silent at 100%, and silent for anyone without
                settings.edit. */}
            <SetupPrompt />
        </>
        </ToastProvider>
    )
}

//Red shoe
//Frozen